import { engToHangul } from './hangulKeys';

/**
 * 짝 이름을 짧은 글자로 바꾼다 — 주소에 붙일 표.
 *
 * 콜라보 몹은 서버가 **반반 그림**으로 합쳐 내보낸다(utils/collabImage.ts). 같은 주소인데
 * 사람이 짝을 바꾸면 그림이 달라져서, 예전에는 캐시를 1시간밖에 못 줬다 — 화면이 부르는
 * 몹 그림 넷 중 하나가 콜라보라 한 시간마다 그만큼 다시 물었다.
 *
 * 주소에 짝 표를 붙이면 **짝이 바뀔 때만 주소가 바뀐다.** 그래서 서버가 30일을 줘도
 * 안전하다. 표는 짧으면 되고 비밀도 아니라 간단한 셈으로 만든다.
 */
export function collabTag(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h).toString(36);
}

export function getMonsterImgPath(monsterMapping: any, m: any) {
  if (!m) return { imgPath: null, displayName: '' };
  let imgPath = null;
  let displayName = m.name;
  if (monsterMapping && m.name && m.attribute) {
    const info = monsterMapping.monsters?.[m.name]?.[m.attribute];
    if (info) {
      if (Array.isArray(info.aliases) && info.aliases.length > 0) {
        const korAliases = info.aliases.filter((a: string) => /[\uac00-\ud7a3]/.test(a));
        if (korAliases.length > 0) {
          korAliases.sort((a: string, b: string) => a.length - b.length);
          displayName = korAliases[0];
        } else {
          displayName = info.aliases[0];
        }
      }
      for (const state of ['SecondAwakened', 'Awakened']) {
        if (info[state]?.filepath) {
          imgPath = info[state].filepath;
          break;
        }
      }
      // 콜라보 짝이 있으면 주소에 표를 붙인다(위 collabTag 주석 참고)
      const mate = monsterMapping.monsters?.[m.name]?.collab;
      if (imgPath && mate) imgPath = `${imgPath}?c=${collabTag(String(mate))}`;
    }
  }
  return { imgPath, displayName };
}

/**
 * 콜라보 짝의 그림 경로. 반반 이미지의 오른쪽 절반에 쓴다.
 *
 * 짝이 없으면 null 이고, 그때 MonsterImage 는 한 장으로 그린다.
 */
export function collabImgOf(monsterMapping: any, info: any, attribute: string): string | null {
  const partner = info?.collab && monsterMapping?.monsters?.[info.collab];
  const byAttr = partner?.[attribute];
  const entry = byAttr?.SecondAwakened || byAttr?.Awakened || byAttr?.Unawaken;
  return entry?.filepath || null;
}

/**
 * 콜라보 짝 중 **원본 쪽은 목록에서 감춘다.**
 *
 * 같은 몹이 두 칸으로 보이던 문제(노바라가 두 개로 떴다). 콜라보 쪽 이름으로 한 칸만
 * 그린다 — 어느 쪽이 콜라보인지는 사람이 사이트에서 짝지을 때 표시한다(`isCollab`).
 *
 * 표시가 없으면(둘 다 모르면) 감추지 않는다 — 잘못 감추는 것보다 두 개로 보이는 게 낫다.
 */
export function hiddenByCollab(monsterMapping: any, name: string, info: any): boolean {
  const partnerName = info?.collab;
  if (!partnerName) return false;
  const partner = monsterMapping?.monsters?.[partnerName];
  if (!partner) return false;
  return partner.isCollab === true && info.isCollab !== true;
}

// 몬스터 셀렉트 공통 필터링 함수
export function getFilteredMonsters(
  monsterMapping: any,
  attr: string,
  star: number,
  search: string
): [string, any, string, string | null][] {
  if (!monsterMapping) return [];
  const ATTRIBUTE_KEYS = ['Water', 'Fire', 'Wind', 'Light', 'Dark'];
  const lowerSearch = search.trim().toLowerCase();
  // 한/영을 안 바꾸고 친 글자(anfqkf → 물발)도 한글 별칭에 맞춰 본다. 첫 글자 자동 대문자(Anfqkf)는 소문자로도 한 번 더
  const korSearch = engToHangul(search.trim()) || '';
  const korSearch2 = engToHangul(lowerSearch) || '';
  /*
   * 가문을 훑는 차례. 서버가 전투기록에 많이 나온 순서(`order`)를 실어 주면 그대로 따른다 —
   * 속성 화면은 그 속성의 차례, 검색은 속성 무관 차례. 화면에서 정렬하지 않는다(정렬하면
   * 창을 열 때마다 수백 칸을 다시 세워 폰에서 버벅였다). 차례에 없는 가문은 뒤에 붙인다.
   */
  const mons = monsterMapping.monsters || {};
  const wanted: string[] | undefined = monsterMapping.order?.[lowerSearch ? 'all' : attr];
  const families = wanted
    ? [...wanted.filter((f) => f in mons), ...Object.keys(mons).filter((f) => !wanted.includes(f))]
    : Object.keys(mons);
  const allMonsters: Array<[string, any]> = families.map((f) => [f, mons[f]]);
  let filteredMonsters: [string, any, string, string | null][] = [];
  if (lowerSearch) {
    allMonsters.forEach(([name, info]: any) => {
      Object.entries(info).forEach(([attribute, v]: any) => {
        if (!ATTRIBUTE_KEYS.includes(attribute)) return;
        const aliases = v?.aliases || [];
        let imgPath: string | null = null;
        if (v?.SecondAwakened && v.SecondAwakened.filepath) {
          imgPath = v.SecondAwakened.filepath;
        } else if (v?.Awakened && v.Awakened.filepath) {
          imgPath = v.Awakened.filepath;
        } else if (v?.Unawaken && v.Unawaken.filepath) {
          imgPath = v.Unawaken.filepath;
        }
        if (
          name.toLowerCase().includes(lowerSearch) ||
          aliases.some((alias: string) => alias.toLowerCase().includes(lowerSearch)) ||
          (korSearch && aliases.some((alias: string) => alias.includes(korSearch) || (!!korSearch2 && alias.includes(korSearch2))))
        ) {
          filteredMonsters.push([name, info, attribute, imgPath]);
        }
      });
    });
    filteredMonsters = filteredMonsters.filter((item, idx, arr) =>
      arr.findIndex(([n, , a]) => n === item[0] && a === item[2]) === idx
    );
  } else {
    filteredMonsters = allMonsters
      .filter(([name, info]: any) => info[attr]?.Awakened && info[attr].Awakened.stars === star)
      .map(([name, info]: any) => {
        let imgPath: string | null = null;
        if (info[attr]?.SecondAwakened && info[attr].SecondAwakened.stars === star && info[attr].SecondAwakened.filepath) {
          imgPath = info[attr].SecondAwakened.filepath;
        } else if (info[attr]?.Awakened && info[attr].Awakened.stars === star && info[attr].Awakened.filepath) {
          imgPath = info[attr].Awakened.filepath;
        }
        return [name, info, attr, imgPath];
      });
  }
  return filteredMonsters;
} 